import { useEffect, useRef, type RefObject } from "react"
import { Maximize, Minimize2, Pause, Play, Volume2, VolumeX } from "lucide-react"
import { formatTimecode } from "@/components/timeline-player/timeline-chrome"
import { useTranslation } from "@/hooks/use-translation"
import { isIOS } from "@/lib/platform"
import type { PlaybackTimeStore } from "@/lib/timeline"
import { cn } from "@/lib/utils"

/** На iOS громкость media-элементов read-only (только аппаратные кнопки) — ползунок там бесполезен. */
const SHOW_VOLUME_SLIDER = !isIOS()

/** Viewport с cursor-pointer перекрывает cursor на шелле — форсим на потомков. */
const FS_HIDE_CURSOR_CLASS = "cursor-none [&_*]:!cursor-none"

export type VideoShellOverlayProps = {
  shellRef: RefObject<HTMLElement | null>
  isFullscreen: boolean
  /** Контролы видимы; в фулскрине управляется idle-таймером родителя (usePointerIdle). */
  controlsVisible: boolean
  onControlsPointerEnter: () => void
  onControlsPointerLeave: () => void
  playing: boolean
  onTogglePlay: () => void
  muted: boolean
  volume: number
  setMuted: (value: boolean | ((prev: boolean) => boolean)) => void
  setVolume: (value: number) => void
  toggleFullscreen: () => void
  timeStore: PlaybackTimeStore
  durationMs: number
  fps: number
  onSeek: (ms: number) => void
  /**
   * Захват ползунка. Контракт scrubTo движка — пауза на pointerdown: без неё
   * scrub-rAF (forceSeek → pause) и play-tick (play) дёргают мастер-видео
   * до 60 раз в секунду друг против друга.
   */
  onScrubStart?: () => void
  /** Промежуточная позиция при перетаскивании ползунка (коалесится в движке). */
  onScrub?: (ms: number) => void
  /** Отпускание ползунка — финальный точный сик. */
  onScrubEnd?: () => void
}

export function VideoShellOverlay({
  shellRef,
  isFullscreen,
  controlsVisible,
  onControlsPointerEnter,
  onControlsPointerLeave,
  playing,
  onTogglePlay,
  muted,
  volume,
  setMuted,
  setVolume,
  toggleFullscreen,
  timeStore,
  durationMs,
  fps,
  onSeek,
  onScrubStart,
  onScrub,
  onScrubEnd,
}: VideoShellOverlayProps) {
  const { t } = useTranslation(["shot"])

  const fillRef = useRef<HTMLDivElement>(null)
  const timecodeRef = useRef<HTMLSpanElement>(null)
  const rangeRef = useRef<HTMLInputElement>(null)
  const scrubbingRef = useRef(false)
  const lastScrubMsRef = useRef(0)

  useEffect(() => {
    const shell = shellRef.current
    if (!shell) return
    const hide = isFullscreen && !controlsVisible
    for (const cls of FS_HIDE_CURSOR_CLASS.split(" ")) {
      shell.classList.toggle(cls, hide)
    }
    return () => {
      for (const cls of FS_HIDE_CURSOR_CLASS.split(" ")) {
        shell.classList.remove(cls)
      }
    }
  }, [shellRef, isFullscreen, controlsVisible])

  useEffect(() => {
    const update = () => {
      if (scrubbingRef.current) return
      const ms = timeStore.get()
      const max = Math.max(durationMs, 1)
      const pct = Math.min(100, Math.max(0, (ms / max) * 100))
      if (fillRef.current) fillRef.current.style.width = `${pct}%`
      if (rangeRef.current && document.activeElement !== rangeRef.current) {
        rangeRef.current.value = String(ms)
      }
      if (timecodeRef.current) {
        const text = formatTimecode(ms, fps)
        if (timecodeRef.current.textContent !== text) {
          timecodeRef.current.textContent = text
        }
      }
    }
    update()
    return timeStore.subscribe(update)
  }, [timeStore, durationMs, fps])

  const seekFromRange = (raw: string) => {
    const next = Number(raw)
    if (!Number.isFinite(next)) return
    const clamped = Math.min(Math.max(next, 0), Math.max(durationMs, 0))
    const max = Math.max(durationMs, 1)
    if (fillRef.current) fillRef.current.style.width = `${(clamped / max) * 100}%`
    if (timecodeRef.current) timecodeRef.current.textContent = formatTimecode(clamped, fps)
    lastScrubMsRef.current = clamped
    // Пока тянем — scrubTo (коалесится), точный сик один раз на отпускании.
    if (scrubbingRef.current && onScrub) onScrub(clamped)
    else onSeek(clamped)
  }

  const endRangeScrub = () => {
    if (!scrubbingRef.current) return
    scrubbingRef.current = false
    if (onScrubEnd) onScrubEnd()
    else onSeek(lastScrubMsRef.current)
  }

  // Страховка: если pointerup ушёл мимо ползунка, флаг иначе залипнет — движок
  // останется в режиме скраба и перестанет показывать индикатор буферизации.
  const beginRangeScrub = () => {
    if (scrubbingRef.current) return
    scrubbingRef.current = true
    onScrubStart?.()
    const finish = () => {
      window.removeEventListener("pointerup", finish)
      window.removeEventListener("pointercancel", finish)
      endRangeScrub()
    }
    window.addEventListener("pointerup", finish)
    window.addEventListener("pointercancel", finish)
  }

  const volumeChrome = (
    <>
      <button
        type="button"
        title={muted ? t("player.unmute") : t("player.mute")}
        className="rounded p-1.5 text-white/90 transition-colors hover:bg-white/15 hover:text-white"
        onClick={() => setMuted((v) => !v)}
      >
        {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
      </button>
      {SHOW_VOLUME_SLIDER ? (
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={muted ? 0 : volume}
          aria-label={t("player.volume")}
          title={t("player.volume")}
          className="h-1 w-20 cursor-pointer accent-white pointer-coarse:h-4"
          onChange={(e) => {
            const next = Number(e.target.value)
            setVolume(next)
            setMuted(next === 0)
          }}
        />
      ) : null}
      <button
        type="button"
        title={isFullscreen ? t("player.exitFullscreen") : t("player.fullscreen")}
        className="rounded p-1.5 text-white/90 transition-colors hover:bg-white/15 hover:text-white"
        onClick={() => void toggleFullscreen()}
      >
        {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
      </button>
    </>
  )

  return (
    <div
      className={cn(
        "absolute inset-x-0 bottom-0 z-30 bg-gradient-to-t from-black/55 to-transparent px-2.5 pt-8 transition-opacity",
        isFullscreen ? "pb-[max(0.625rem,env(safe-area-inset-bottom))]" : "pb-2.5",
        // В фулскрине видимость (в т.ч. на таче) диктует idle-таймер: контролы прячутся
        // по бездействию во время воспроизведения.
        isFullscreen
          ? cn(controlsVisible ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0")
          : cn(
              "pointer-events-none opacity-0",
              "group-focus-within/video:pointer-events-auto group-focus-within/video:opacity-100",
              "group-hover/video:pointer-events-auto group-hover/video:opacity-100",
              // Тач вне фулскрина: hover нет — контролы видны всегда (события ловят только
              // сами контролы, чтобы тап по видео под градиентом оставался play/pause).
              "pointer-coarse:opacity-100",
            ),
      )}
      onPointerEnter={isFullscreen ? onControlsPointerEnter : undefined}
      onPointerLeave={isFullscreen ? onControlsPointerLeave : undefined}
    >
      {isFullscreen ? (
        <div
          className="flex w-full flex-col gap-2"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="relative h-1.5 w-full">
            <div className="h-full overflow-hidden rounded-full bg-white/25">
              <div ref={fillRef} className="h-full rounded-full bg-white" style={{ width: "0%" }} />
            </div>
            <input
              ref={rangeRef}
              type="range"
              min={0}
              max={Math.max(durationMs, 1)}
              step={1}
              defaultValue={0}
              aria-label={t("player.shortcutsSeek")}
              className="absolute inset-x-0 -top-2 -bottom-2 w-full cursor-pointer opacity-0"
              onPointerDown={beginRangeScrub}
              onChange={(e) => seekFromRange(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              title={playing ? t("player.pause") : t("player.play")}
              className="rounded p-1.5 text-white/90 transition-colors hover:bg-white/15 hover:text-white"
              onClick={onTogglePlay}
            >
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>
            <span className="font-mono text-[11px] text-white/85 tabular-nums" title={t("player.timecode")}>
              <span ref={timecodeRef} />
              <span className="mx-1 text-white/50">/</span>
              {formatTimecode(durationMs, fps)}
            </span>
            <div className="ml-auto flex items-center gap-1 rounded-lg bg-black/55 px-1.5 py-1 backdrop-blur-sm">
              {volumeChrome}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex justify-end">
          <div
            className="flex items-center gap-1 rounded-lg bg-black/55 px-1.5 py-1 backdrop-blur-sm pointer-coarse:pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {volumeChrome}
          </div>
        </div>
      )}
    </div>
  )
}
